import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { MinusOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Col, Form, Row, Typography } from 'antd'
import { get, includes, map, size, uniq } from 'lodash'
import PropTypes from 'prop-types'

import {
  FormLazyTreeSelect,
  FormTextareaInput,
} from '../../../common/components/sections'
import { treeOptionPropType } from '../../../common/prop-types'
import { formatFormLabel, formatFormListLabel } from '../../../common/utils'

const DishTypeAlternativeIngredientsFormList = ({
  ingredientOptions,
  groupedFlatIngredientTreeDataByValue,
  flatIngredientTreeData,
  initialValues,
  form,
  isDirty,
  disabled,
}) => {
  const name = 'alternativeIngredients'
  const [listSize, setListSize] = useState(size(form.getFieldValue(name)))

  const ingredientIdRules = useMemo(
    () => [
      ({ getFieldValue }) => ({
        validator(_, value) {
          const alternativeIngredientPairs = getFieldValue(
            'alternativeIngredients',
          )

          const alternativeIngredientIds = map(
            alternativeIngredientPairs,
            'alternativeIngredientId',
          )

          const ingredientIds = map(alternativeIngredientPairs, 'ingredientId')

          if (includes(alternativeIngredientIds, value)) {
            return Promise.reject(
              new Error(
                'Item is already present as an alternative ingredients',
              ),
            )
          }

          if (size(uniq([...ingredientIds, value])) !== size(ingredientIds)) {
            return Promise.reject(new Error('Item already has an alternative'))
          }

          return Promise.resolve()
        },
      }),
    ],
    [],
  )

  const alternativeIngredientId = useMemo(
    () => [
      ({ getFieldValue }) => ({
        validator(_, value) {
          const alternativeIngredientPairs = getFieldValue(
            'alternativeIngredients',
          )

          const ingredientIds = map(alternativeIngredientPairs, 'ingredientId')

          if (includes(ingredientIds, value)) {
            return Promise.reject(new Error('Item is already an alternative'))
          }

          return Promise.resolve()
        },
      }),
    ],
    [],
  )

  const labelContent = useMemo(() => {
    const formListLabel = formatFormListLabel(
      `Replacement Pairs`,
      listSize,
      true,
    )

    return formatFormLabel(formListLabel, true)
  }, [listSize])

  const handleAddItem = useCallback(
    add => () => {
      add()
      setListSize(previousSize => previousSize + 1)
    },
    [],
  )
  const handleRemoveItemByName = useCallback(
    (name, remove) => () => {
      remove(name)
      setListSize(previousSize => previousSize - 1)
    },
    [],
  )

  useEffect(() => {
    setListSize(size(form.getFieldValue(name)))
  }, [form, isDirty])

  return (
    <>
      <Typography.Paragraph>{labelContent}</Typography.Paragraph>
      <Form.List name={name}>
        {(fields, { add, remove }) => (
          <Row wrap gutter={[16]}>
            {map(fields, ({ key, name }) => (
              <Col span={24} key={key}>
                <Row gutter={[16]} align="top">
                  <Col span={8}>
                    <FormLazyTreeSelect
                      disabled={disabled}
                      form={form}
                      name={[name, 'ingredientId']}
                      showColon={false}
                      showSearch
                      allowClear
                      treeData={ingredientOptions}
                      placeholder="Select an ingredient to replace"
                      groupedFlatTreeDataByValue={
                        groupedFlatIngredientTreeDataByValue
                      }
                      flatTreeData={flatIngredientTreeData}
                      initialValue={get(initialValues, `${name}.ingredientId`)}
                      rules={ingredientIdRules}
                    />
                  </Col>
                  <Col span={8}>
                    <FormLazyTreeSelect
                      disabled={disabled}
                      form={form}
                      name={[name, 'alternativeIngredientId']}
                      showColon={false}
                      showSearch
                      allowClear
                      treeData={ingredientOptions}
                      placeholder="Select a replacement"
                      groupedFlatTreeDataByValue={
                        groupedFlatIngredientTreeDataByValue
                      }
                      flatTreeData={flatIngredientTreeData}
                      initialValue={get(
                        initialValues,
                        `${name}.alternativeIngredientId`,
                      )}
                      rules={alternativeIngredientId}
                    />
                  </Col>
                  <Col span={7}>
                    <FormTextareaInput
                      disabled={disabled}
                      name={[name, 'notes']}
                      placeholder="Add notes for this replacement pair..."
                      showColon={false}
                      autoSize={{ minRows: 1, maxRows: 4 }}
                      style={{ resize: 'vertical' }}
                    />
                  </Col>
                  <Col span={1}>
                    <Button
                      disabled={disabled}
                      icon={<MinusOutlined />}
                      shape="circle"
                      onClick={handleRemoveItemByName(name, remove)}
                    />
                  </Col>
                </Row>
              </Col>
            ))}
            <Col>
              <Form.Item>
                <Button
                  disabled={disabled}
                  type="dashed"
                  onClick={handleAddItem(add)}
                  icon={<PlusOutlined />}
                >
                  Add Replacement Pair
                </Button>
              </Form.Item>
            </Col>
          </Row>
        )}
      </Form.List>
    </>
  )
}

DishTypeAlternativeIngredientsFormList.propTypes = {
  form: PropTypes.object.isRequired,
  isDirty: PropTypes.bool,
  ingredientOptions: PropTypes.arrayOf(treeOptionPropType).isRequired,
  groupedFlatIngredientTreeDataByValue: PropTypes.object.isRequired,
  flatIngredientTreeData: PropTypes.arrayOf(PropTypes.object).isRequired,
  initialValues: PropTypes.arrayOf(
    PropTypes.shape({
      ingredientId: PropTypes.string,
      alternativeIngredientId: PropTypes.string.isRequired,
      notes: PropTypes.string,
    }),
  ),
  disabled: PropTypes.bool,
}

DishTypeAlternativeIngredientsFormList.defaultProps = {
  isDirty: false,
  initialValues: {},
  disabled: false,
}

export default DishTypeAlternativeIngredientsFormList
